"use client";

/**
 * VectorPatternGenerator — a third background source for the theme editor
 * (alongside Skins and My GIFs). Per Gordon: "as a third custom we need a
 * vector generator for all the background like for images."
 *
 * Generates a tileable SVG pattern as a data-URL and hands it to the GIF/image
 * library via onAdd(), so it flows through the exact same section-assignment +
 * cover/fill/tile pipeline as any other background. Pure — no network, no deps.
 */

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Shuffle } from "lucide-react";
import { cn } from "@/lib/utils";

type PatternKind =
  | "dots" | "grid" | "stripes" | "diagonal" | "waves"
  | "triangles" | "hexagons" | "crosses" | "confetti" | "circuit";

const KINDS: { id: PatternKind; label: string }[] = [
  { id: "dots", label: "Dots" },
  { id: "grid", label: "Grid" },
  { id: "stripes", label: "Stripes" },
  { id: "diagonal", label: "Diagonal" },
  { id: "waves", label: "Waves" },
  { id: "triangles", label: "Triangles" },
  { id: "hexagons", label: "Hexagons" },
  { id: "crosses", label: "Crosses" },
  { id: "confetti", label: "Confetti" },
  { id: "circuit", label: "Circuit" },
];

/** Build the inner SVG markup for one tile of the chosen pattern. */
function patternBody(kind: PatternKind, t: number, fg: string, sw: number): string {
  const c = t / 2;
  switch (kind) {
    case "dots":
      return `<circle cx="${c}" cy="${c}" r="${t * 0.16}" fill="${fg}"/>`;
    case "grid":
      return `<path d="M0 0H${t}M0 0V${t}" stroke="${fg}" stroke-width="${sw}" fill="none"/>`;
    case "stripes":
      return `<rect x="0" y="0" width="${t}" height="${t / 2}" fill="${fg}"/>`;
    case "diagonal":
      return `<path d="M-1 1 l2 -2 M0 ${t} l${t} -${t} M${t - 1} ${t + 1} l2 -2" stroke="${fg}" stroke-width="${sw * 2}"/>`;
    case "waves":
      return `<path d="M0 ${c} q ${c / 2} -${c / 2} ${c} 0 t ${c} 0" stroke="${fg}" stroke-width="${sw}" fill="none"/>`;
    case "triangles":
      return `<path d="M${c} ${t * 0.2} L${t * 0.8} ${t * 0.8} L${t * 0.2} ${t * 0.8} Z" fill="${fg}"/>`;
    case "hexagons": {
      const r = t * 0.32;
      const pts = Array.from({ length: 6 }, (_, i) => {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        return `${(c + r * Math.cos(a)).toFixed(1)},${(c + r * Math.sin(a)).toFixed(1)}`;
      }).join(" ");
      return `<polygon points="${pts}" fill="none" stroke="${fg}" stroke-width="${sw}"/>`;
    }
    case "crosses":
      return `<path d="M${c} ${t * 0.25}V${t * 0.75}M${t * 0.25} ${c}H${t * 0.75}" stroke="${fg}" stroke-width="${sw * 1.5}"/>`;
    case "confetti":
      return `<rect x="${t * 0.15}" y="${t * 0.2}" width="${t * 0.18}" height="${t * 0.18}" fill="${fg}" transform="rotate(20 ${t * 0.24} ${t * 0.29})"/>`
        + `<circle cx="${t * 0.7}" cy="${t * 0.65}" r="${t * 0.1}" fill="${fg}" opacity="0.7"/>`;
    case "circuit":
      return `<path d="M0 ${c}H${c}V0 M${c} ${t}V${c}H${t}" stroke="${fg}" stroke-width="${sw}" fill="none"/>`
        + `<circle cx="${c}" cy="${c}" r="${t * 0.07}" fill="${fg}"/>`;
  }
}

/** Compose a full tileable SVG and return it as a data-URL.
 *  shapeScale scales the motif within the tile (size vs spacing), rotation/
 *  skew/offset transform it, shapeOpacity fades it (Gordon: "size of dots,
 *  distance between the dots, skew, offset etc"). */
function buildSvgDataUrl(
  kind: PatternKind, tile: number, fg: string, bg: string, stroke: number,
  shapeScale = 100, rotation = 0, skew = 0, offsetX = 0, offsetY = 0, shapeOpacity = 100,
): string {
  const body = patternBody(kind, tile, fg, stroke);
  const c = tile / 2;
  const sc = shapeScale / 100;
  const transform = [
    `translate(${(offsetX / 100) * tile} ${(offsetY / 100) * tile})`,
    `rotate(${rotation} ${c} ${c})`,
    skew ? `skewX(${skew})` : '',
    sc !== 1 ? `translate(${c} ${c}) scale(${sc}) translate(${-c} ${-c})` : '',
  ].filter(Boolean).join(' ');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${tile}" height="${tile}" viewBox="0 0 ${tile} ${tile}">` +
    `<rect width="${tile}" height="${tile}" fill="${bg}"/>` +
    `<g transform="${transform}" opacity="${shapeOpacity / 100}">${body}</g>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

interface Props {
  /** Add the generated pattern to the background library as a usable entry */
  onAdd: (entry: { id: string; url: string; label: string }) => void;
  /** Pin the pattern straight onto a section (also adds it to the library). */
  onPinToSection?: (section: string, url: string) => void;
  sectionLabels?: Record<string, string>;
  sections?: readonly string[];
}

export function VectorPatternGenerator({ onAdd, onPinToSection, sectionLabels, sections }: Props) {
  const [kind, setKind] = useState<PatternKind>("dots");
  const [tile, setTile] = useState(48);
  const [fg, setFg] = useState("#10b981");
  const [bg, setBg] = useState("#0b0f0d");
  const [stroke, setStroke] = useState(2);
  const [shapeScale, setShapeScale] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [skew, setSkew] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [shapeOpacity, setShapeOpacity] = useState(100);

  const dataUrl = useMemo(
    () => buildSvgDataUrl(kind, tile, fg, bg, stroke, shapeScale, rotation, skew, offsetX, offsetY, shapeOpacity),
    [kind, tile, fg, bg, stroke, shapeScale, rotation, skew, offsetX, offsetY, shapeOpacity]
  );

  const randomize = () => {
    const k = KINDS[Math.floor(Math.random() * KINDS.length)].id;
    const hues = ["#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#eab308", "#22c55e"];
    setKind(k);
    setFg(hues[Math.floor(Math.random() * hues.length)]);
    setTile(24 + Math.floor(Math.random() * 80));
    setStroke(1 + Math.floor(Math.random() * 4));
    setShapeScale(50 + Math.floor(Math.random() * 120));
    setRotation(Math.floor(Math.random() * 90));
    setSkew(Math.floor(Math.random() * 30) - 15);
    setOffsetX(0); setOffsetY(0);
    setShapeOpacity(60 + Math.floor(Math.random() * 40));
  };

  const add = () => {
    onAdd({
      id: `vec-${Date.now()}`,
      url: dataUrl,
      label: `${KINDS.find(k => k.id === kind)?.label} ${tile}px`,
    });
  };

  return (
    <div className="px-3 pb-3 space-y-3">
      {/* Live preview — tiled so you see the repeat */}
      <div
        className="w-full h-28 rounded-lg border border-border"
        style={{ backgroundImage: `url("${dataUrl}")`, backgroundRepeat: "repeat", backgroundSize: `${tile}px` }}
      />

      {/* Pattern kinds */}
      <div className="grid grid-cols-5 gap-1">
        {KINDS.map(k => (
          <button
            key={k.id}
            onClick={() => setKind(k.id)}
            className={cn(
              "px-1 py-1 rounded text-[9px] font-medium border transition-colors",
              kind === k.id ? "bg-primary text-white border-primary" : "border-border/50 text-muted-foreground hover:text-foreground"
            )}
          >{k.label}</button>
        ))}
      </div>

      {/* Colors */}
      <div className="grid grid-cols-2 gap-2">
        <label className="flex items-center gap-2 text-[10px] text-muted-foreground">
          Pattern
          <input type="color" value={fg} onChange={e => setFg(e.target.value)} className="h-6 w-8 rounded border border-border bg-transparent p-0" />
        </label>
        <label className="flex items-center gap-2 text-[10px] text-muted-foreground">
          Background
          <input type="color" value={bg} onChange={e => setBg(e.target.value)} className="h-6 w-8 rounded border border-border bg-transparent p-0" />
        </label>
      </div>

      {/* Parameters — spacing, size, weight, rotation, skew, offset, opacity */}
      {([
        ["Spacing (tile)", tile, 16, 200, "px", (v: number) => setTile(v)],
        ["Shape size", shapeScale, 20, 220, "%", (v: number) => setShapeScale(v)],
        ["Line / detail weight", stroke, 1, 8, "px", (v: number) => setStroke(v)],
        ["Rotation", rotation, 0, 180, "°", (v: number) => setRotation(v)],
        ["Skew", skew, -45, 45, "°", (v: number) => setSkew(v)],
        ["Offset X", offsetX, -50, 50, "%", (v: number) => setOffsetX(v)],
        ["Offset Y", offsetY, -50, 50, "%", (v: number) => setOffsetY(v)],
        ["Shape opacity", shapeOpacity, 10, 100, "%", (v: number) => setShapeOpacity(v)],
      ] as [string, number, number, number, string, (v: number) => void][]).map(([label, val, min, max, unit, set]) => (
        <div key={label} className="space-y-1">
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{label}</span><span>{val}{unit}</span>
          </div>
          <input type="range" min={min} max={max} value={val} onChange={e => set(Number(e.target.value))} className="w-full accent-primary" />
        </div>
      ))}

      {/* Pin straight onto a section */}
      {onPinToSection && sections && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pin to section</p>
          <div className="flex flex-wrap gap-1">
            {sections.map(sec => (
              <button
                key={sec}
                onClick={() => onPinToSection(sec, dataUrl)}
                className="text-[9px] px-2 py-1 rounded-full border border-border/50 hover:border-primary hover:text-primary transition-colors"
              >
                {sectionLabels?.[sec] || sec}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="flex-1 h-8 text-xs gap-1" onClick={randomize}>
          <Shuffle className="h-3 w-3" /> Random
        </Button>
        <Button size="sm" className="flex-1 h-8 text-xs gap-1" onClick={add}>
          <Plus className="h-3 w-3" /> Add to library
        </Button>
      </div>
      <p className="text-[9px] text-muted-foreground/70 text-center">
        Added vectors behave like any background — assign them per section and use Cover / Fill / Tile below.
      </p>
    </div>
  );
}
